import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { categorySampleData } from '../shared/sample-data/category';
import { CommonService } from './common.service';

export interface CategoryAnalysisRequest {
  brands: string[];
  category?: string;
  region?: string;
  include_competitors?: boolean;
  force_refresh?: boolean;
}

export interface CategoryAnalysisResponse {
  brands: Array<{
    name: string;
    composite_score: number;
    category_scores: number[];
    is_your_brand: boolean;
  }>;
  chart_data: {
    composite_score: {
      value: number;
      label: string;
      color: string;
      brand: string;
    };
    ai_category_insights: {
      title: string;
      type: string;
      x_axis: string[];
      series: Array<{
        name: string;
        type: string;
        data: number[];
      }>;
    };
    ai_table_summary?: {
      title: string;
      headers: string[];
      rows: any[][];
    };
    category_demand?: {
      title: string;
      type: string;
      series: Array<{
        name: string;
        value: number;
        rate: number;
        forecast: number;
      }>;
    };
    subject_demand_analysis?: {
      title: string;
      type: string;
      series: Array<{
        name: string;
        data: Record<string, any>;
      }>;
    };
    university_info?: Record<string, any>;
    recommendations?: {
      strengths: string[];
      weaknesses: string[];
    };
  };
  context: {
    analysis_type: string;
    category: string;
    region: string;
    total_brands_analyzed: number;
  };
  timestamp: string;
  model_meta: {
    model: string;
    response_time: number;
  };
  citations?: Array<{
    source: string;
    url: string;
    title: string;
    snippet: string;
    confidence: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  private readonly apiUrl = `${environment.apiUrl}`;

  constructor(private http: HttpClient, private commonService: CommonService) {}

  /**
   * Analyze category with composite scores, charts, and tables
   */
  analyzeCategory(request: CategoryAnalysisRequest): Observable<CategoryAnalysisResponse> {
    const isGeneric = this.commonService.isGenericFlowActive();
    if (isGeneric) {
      // Use the new generic API endpoint
      return this.http.post<CategoryAnalysisResponse>(
        `${this.apiUrl}/generic/category/analyze`,
        request
      );
    }

    // Legacy education API - convert to new format for consistency
    const educationPayload: CategoryAnalysisRequest = {
      brands: request.brands,
      category: request.category || 'Education',
      region: request.region || 'Global',
      include_competitors: request.include_competitors || true,
      force_refresh: request.force_refresh || false
    };
    return this.http.post<CategoryAnalysisResponse>(`${this.apiUrl}/education/category/analyze`, educationPayload);
  }

  /**
   * Get sample data for development/testing
   */
  getSampleData(): Observable<CategoryAnalysisResponse> {
    return of(categorySampleData);
  }

}
